import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Monitor, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase, getSupabaseUrl } from '../supabase/client';

interface AlipayPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
  orderNo: string;
  amount: number;
  subject: string;
  onSuccess: () => void;
  onFail: () => void;
}

type PaymentStatus = 'idle' | 'loading' | 'qr_ready' | 'success' | 'fail' | 'expired';

export default function AlipayPaymentModal({
  isOpen,
  onClose,
  orderId,
  orderNo,
  amount,
  subject,
  onSuccess,
  onFail,
}: AlipayPaymentModalProps) {
  const [status, setStatus] = useState<PaymentStatus>('idle');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [error, setError] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const [timeLeft, setTimeLeft] = useState('');
  const maxRetries = 3;
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPollingRef = useRef(false);
  const expireTimeRef = useRef<Date | null>(null);

  const createOrder = useCallback(async () => {
    setStatus('loading');
    setError('');
    setQrCodeUrl('');

    try {
      const session = (await supabase.auth.getSession()).data.session;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }
      const userId = session?.user?.id;

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/alipay-service`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          action: 'createOrder',
          orderId,
          orderNo,
          amount,
          subject,
          isMobile: false,
          userId,
        }),
      });

      const result = await response.json();

      if (!result.success) throw new Error(result.error || '创建订单失败');

      if (result.expireTime) {
        expireTimeRef.current = new Date(result.expireTime);
      }

      // 处理 page.pay 返回的表单（PC端电脑网站支付）
      if (result.type === 'page' && result.formHtml) {
        // 将表单写入新窗口并自动提交
        const payWindow = window.open('', '_blank');
        if (payWindow) {
          payWindow.document.write(result.formHtml);
          payWindow.document.close();
        } else {
          throw new Error('浏览器阻止了弹窗，请允许弹窗后重试');
        }
        // 开始轮询订单状态
        startAutoPolling();
        return;
      }

      // 处理 qr 类型（二维码支付）
      const qrContent = result.qrCodeUrl || result.qrCode;
      if (!qrContent) {
        throw new Error('未获取到支付信息');
      }

      // 使用公开二维码API生成二维码图片
      const qrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(qrContent)}`;
      setQrCodeUrl(qrImageUrl);
      setStatus('qr_ready');
      startAutoPolling();
    } catch (err: any) {
      setError(err.message || '创建订单失败');
      setStatus('fail');
    }
  }, [orderId, orderNo, amount, subject]);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
    isPollingRef.current = false;
  }, []);

  const onSuccessRef = useRef(onSuccess);
  useEffect(() => { onSuccessRef.current = onSuccess; }, [onSuccess]);

  const startAutoPolling = useCallback(() => {
    if (isPollingRef.current) return;
    isPollingRef.current = true;

    let pollCount = 0;
    const maxPolls = 600;
    const currentOrderNo = orderNo;

    const poll = async () => {
      if (!isPollingRef.current) return;
      if (pollCount >= maxPolls) {
        setStatus('expired');
        isPollingRef.current = false;
        return;
      }

      try {
        const session = (await supabase.auth.getSession()).data.session;
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (session?.access_token) {
          headers['Authorization'] = `Bearer ${session.access_token}`;
        }

        const response = await fetch(`${getSupabaseUrl()}/functions/v1/alipay-service`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ action: 'queryOrder', orderNo: currentOrderNo }),
        });

        const result = await response.json();

        if (result.status === 'paid') {
          setStatus('success');
          isPollingRef.current = false;
          setTimeout(() => onSuccessRef.current(), 500);
          return;
        } else if (result.status === 'expired') {
          setStatus('expired');
          isPollingRef.current = false;
          return;
        }
      } catch (err) {
        // ignore
      }

      pollCount++;
      if (isPollingRef.current) {
        pollingRef.current = setTimeout(poll, 2000);
      }
    };

    poll();
  }, [orderNo]);

  const handleRetry = async () => {
    if (retryCount >= maxRetries) {
      setError('重试次数已达上限，请稍后重试或联系客服');
      return;
    }
    setRetryCount(prev => prev + 1);
    await createOrder();
  };

  // 倒计时
  useEffect(() => {
    if (status !== 'qr_ready') return;
    const interval = setInterval(() => {
      if (!expireTimeRef.current) return;
      const diff = expireTimeRef.current.getTime() - Date.now();
      if (diff <= 0) {
        setTimeLeft('00:00');
        setStatus('expired');
        clearInterval(interval);
        return;
      }
      const m = Math.floor(diff / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);
    }, 1000);
    return () => clearInterval(interval);
  }, [status]);

  useEffect(() => {
    if (isOpen && status === 'idle') {
      createOrder();
    }
  }, [isOpen, status, createOrder]);

  useEffect(() => {
    if (!isOpen) {
      stopPolling();
      setStatus('idle');
      setQrCodeUrl('');
      setError('');
    }
  }, [isOpen, stopPolling]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl"
        >
          {/* 头部 */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">支</span>
              </div>
              <h2 className="text-lg font-semibold text-gray-900">支付宝支付</h2>
            </div>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6">
            {/* 金额 */}
            <div className="text-center mb-6">
              <p className="text-sm text-gray-500 mb-1">支付金额</p>
              <p className="text-3xl font-bold text-gray-900">¥{amount.toFixed(2)}</p>
              <p className="text-sm text-gray-500 mt-1">{subject}</p>
            </div>

            {/* Loading */}
            {status === 'loading' && (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-10 h-10 text-blue-500 animate-spin mb-4" />
                <p className="text-gray-600">正在生成二维码...</p>
              </div>
            )}

            {/* 二维码 */}
            {status === 'qr_ready' && qrCodeUrl && (
              <div className="flex flex-col items-center">
                <div className="bg-white rounded-xl p-4 mb-4 border border-gray-100 shadow-sm">
                  <img src={qrCodeUrl} alt="支付宝扫码支付" className="w-64 h-64" />
                </div>
                <p className="text-sm text-gray-600 flex items-center gap-2 mb-1">
                  <Monitor className="w-4 h-4" />
                  请使用支付宝扫一扫
                </p>
                <p className="text-xs text-blue-500 mb-2">支付成功后将自动跳转</p>
                {timeLeft && (
                  <p className="text-xs text-orange-500">剩余支付时间: {timeLeft}</p>
                )}
              </div>
            )}

            {/* 成功 */}
            {status === 'success' && (
              <div className="flex flex-col items-center justify-center py-8">
                <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
                <p className="text-lg font-semibold text-gray-900">支付成功</p>
                <p className="text-sm text-gray-500 mt-1">正在为您开通权益...</p>
              </div>
            )}

            {/* 失败 */}
            {(status === 'fail' || (error && status !== 'qr_ready')) && (
              <div className="flex flex-col items-center justify-center py-8">
                <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
                <p className="text-lg font-semibold text-gray-900">支付失败</p>
                <p className="text-sm text-red-500 mt-1">{error || '请稍后重试'}</p>
                {retryCount < maxRetries && (
                  <button onClick={handleRetry} className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
                    重新支付 ({retryCount}/{maxRetries})
                  </button>
                )}
                {retryCount >= maxRetries && (
                  <div className="mt-4 text-center">
                    <p className="text-sm text-gray-500">重试次数已达上限</p>
                    <button onClick={onClose} className="mt-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors">
                      关闭窗口
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* 过期 */}
            {status === 'expired' && (
              <div className="flex flex-col items-center justify-center py-8">
                <AlertCircle className="w-16 h-16 text-orange-500 mb-4" />
                <p className="text-lg font-semibold text-gray-900">订单已过期</p>
                <p className="text-sm text-gray-500 mt-1">支付超时，请重新下单</p>
                <button onClick={onClose} className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
                  返回订单
                </button>
              </div>
            )}

            {/* 底部信息 */}
            <div className="mt-6 flex items-center justify-center gap-4 text-xs text-gray-400">
              <span className="flex items-center gap-1">
                <Monitor className="w-3 h-3" />
                PC端
              </span>
              <span>订单号: {orderNo.slice(-8)}</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
